/**
 * ChannelSidebar — three fixed channels, then two rosters (spec #44 §1).
 *
 * The agents roster is rendered in SEED_AGENTS order and an agent whose
 * `configured` is false is rendered GREYED and labelled "not set up",
 * never hidden: a tenant has to be able to see the team it does not have
 * yet. Specialists are rows, not channels — each opens its owner's
 * channel, because that is where it reports.
 */

import { Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import { useWorkspace } from '../../hooks/useWorkspace';
import {
  NEEDS_YOU_CHANNEL,
  TEAM_NOW_CHANNEL,
} from '../../types/workspace';
import { ChannelRow } from './ChannelRow';
import styles from './workspace.module.css';

export function ChannelSidebar() {
  const {
    agents,
    specialists,
    openDomain,
    openChannel,
    presenceFor,
    unreadFor,
    needsYouCount,
  } = useWorkspace();

  const enabledSpecialists = specialists.filter((s) => s.enabled);

  return (
    <nav className={styles.sidebar} aria-label="Channels">
      <div className={styles.groupLabel}>Channels</div>
      <ChannelRow
        testId="channel-row-board"
        sigil="▦"
        label="board"
        open={openDomain === null}
        onOpen={() => openChannel(null)}
      />
      <ChannelRow
        testId="channel-row-needs-you"
        sigil="#"
        label="needs-you"
        open={openDomain === NEEDS_YOU_CHANNEL}
        unreadKey={NEEDS_YOU_CHANNEL}
        unreadCount={needsYouCount}
        onOpen={() => openChannel(NEEDS_YOU_CHANNEL)}
      />
      <ChannelRow
        testId="channel-row-team-now"
        sigil="#"
        label="team-now"
        open={openDomain === TEAM_NOW_CHANNEL}
        onOpen={() => openChannel(TEAM_NOW_CHANNEL)}
      />

      <div className={styles.groupLabel}>Agents</div>
      {agents.length === 0 ? (
        <div className={styles.subtle} style={{ padding: '4px 8px' }}>
          <Text as="p" tone="subdued">
            No agents are set up yet
          </Text>
        </div>
      ) : (
        agents.map((agent) => (
          <ChannelRow
            key={agent.domain}
            testId={`channel-row-agent-${agent.domain}`}
            label={agent.display_name}
            presence={presenceFor(agent.domain)}
            open={openDomain === agent.domain}
            unread={unreadFor(agent.domain)}
            unreadKey={agent.domain}
            onOpen={() => openChannel(agent.domain)}
          />
        ))
      )}

      <div className={styles.groupLabel}>Specialists</div>
      {enabledSpecialists.length === 0 ? (
        <div className={styles.subtle} style={{ padding: '4px 8px' }}>
          <Text as="p" tone="subdued">
            No specialists enabled
          </Text>
        </div>
      ) : (
        enabledSpecialists.map((specialist) => (
          <ChannelRow
            key={specialist.key}
            testId={`channel-row-specialist-${specialist.key}`}
            label={specialist.name}
            indented
            // It reports in its owner's channel, so that is what opens.
            onOpen={() => openChannel(specialist.domain)}
          />
        ))
      )}

      <div className={styles.sidebarFooter}>
        <Link to="/marketing-agents/specialists">
          Browse all specialists →
        </Link>
      </div>
    </nav>
  );
}
